import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isAuthenticated } from "../utils/auth";
import { auth, requestNotificationPermission } from "../config/firebase";
import {
  getUserByMobile,
  registerFCMToken,
  updateFCMToken,
  validateMobile,
  getMembershipStatus,
  registerUserApi,
  activateFreeMembership,
} from "../../services/coreService";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
} from "firebase/auth";
import { FaArrowLeft, FaRedo } from "react-icons/fa";
import ButtonSpinner from "../common/ButtonSpinner";
import Toast from "../common/Toast";
import { useTranslation } from "react-i18next";
import styles from "../css/LoginScreen.module.css";

const LOGO = "/bhada24-logo.png";

function OtpInput({ value, onChange }) {
  const inputs = Array(6).fill("");
  value.split("").forEach((d, i) => { inputs[i] = d; });

  // Web OTP API – auto-read SMS on supported Android browsers
  useEffect(() => {
    let abortController;
    if ("OTPCredential" in window) {
      abortController = new AbortController();
      navigator.credentials
        .get({ otp: { transport: ["sms"] }, signal: abortController.signal })
        .then((otp) => {
          if (otp?.code) {
            const digits = otp.code.replace(/\D/g, "").slice(0, 6);
            onChange(digits);
            if (digits.length === 6) document.getElementById("otp-input-5")?.focus();
          }
        })
        .catch(() => {});
    }
    return () => { try { abortController?.abort(); } catch (_) {} };
  }, [onChange]);

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/, "");
    const arr = value.split("");
    arr[idx] = val;
    onChange(arr.join("").slice(0, 6));
    if (val && idx < 5) document.getElementById(`otp-input-${idx + 1}`)?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !inputs[idx] && idx > 0)
      document.getElementById(`otp-input-${idx - 1}`)?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const d = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(d);
    if (d.length === 6) document.getElementById("otp-input-5")?.focus();
  };

  return (
    <div className={styles.otpContainer} onPaste={handlePaste}>
      {inputs.map((digit, idx) => (
        <input
          key={idx}
          id={`otp-input-${idx}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          className={styles.otpInput}
          autoFocus={idx === 0}
          autoComplete={idx === 0 ? "one-time-code" : "off"}
        />
      ))}
    </div>
  );
}

export default function LoginScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState("chooser");

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [verificationId, setVerificationId] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [loginLoading, setLoginLoading] = useState(false);

  const [registerForm, setRegisterForm] = useState({ name: "", email: "", phone: "", role: "SP" });
  const [registerLoading, setRegisterLoading] = useState(false);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) navigate("/dashboard");
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch (_) {}
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  useEffect(() => {
    let timer;
    if (resendTimer > 0) timer = setTimeout(() => setResendTimer((p) => p - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const clearAlert = () => { setMessage(""); setMessageType(""); setShowToast(false); };

  const openAuthForm = (mode) => {
    clearAlert();
    window.scrollTo({ top: 0, behavior: "instant" });
    setAuthMode(mode);
    navigate(mode === "register" ? "/register" : "/login");
  };

  const goToChooser = () => {
    clearAlert();
    window.scrollTo({ top: 0, behavior: "instant" });
    setAuthMode("chooser");
    setOtpSent(false);
    setOtp("");
    setResendTimer(0);
    navigate("/login");
  };

  const setupRecaptcha = () => {
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch (_) {}
      window.recaptchaVerifier = null;
    }
    const container = document.getElementById("recaptcha-container");
    if (!container) return;
    try {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => {},
        "expired-callback": () => {
          if (window.recaptchaVerifier) {
            try { window.recaptchaVerifier.clear(); } catch (_) {}
            window.recaptchaVerifier = null;
          }
        },
      });
    } catch (_) { window.recaptchaVerifier = null; }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!phone || phone.length !== 10) {
      setMessage(t("LoginScreen.enter_valid_phone", "Please enter a valid 10-digit phone number."));
      setMessageType("error"); setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
      return;
    }
    setLoginLoading(true); clearAlert();
    try {
      const vr = await validateMobile(phone);
      if (vr.responseCode === 200) {
        if (!vr.responseData[0]) {
          setMessage(t("LoginScreen.mobile_not_registered", "Mobile number not registered. Please sign up first."));
          setMessageType("error"); setShowToast(true);
          setTimeout(() => setShowToast(false), 4000);
          setLoginLoading(false); return;
        }
      } else {
        setMessage(t("LoginScreen.validation_failed", "Failed to validate mobile number."));
        setMessageType("error"); setShowToast(true);
        setTimeout(() => setShowToast(false), 4000);
        setLoginLoading(false); return;
      }
      if (window.recaptchaVerifier) { try { window.recaptchaVerifier.clear(); } catch (_) {} window.recaptchaVerifier = null; }
      await new Promise((r) => setTimeout(r, 100));
      setupRecaptcha();
      if (!window.recaptchaVerifier) throw new Error("Failed to initialize reCAPTCHA");
      const result = await signInWithPhoneNumber(auth, `+91${phone}`, window.recaptchaVerifier);
      setVerificationId(result.verificationId);
      setOtpSent(true); setResendTimer(45);
      setMessage(t("LoginScreen.otp_sent_success", "OTP sent successfully!"));
      setMessageType("success"); setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (err) {
      const msg =
        err.code === "auth/invalid-app-credential" ? t("LoginScreen.firebase_config_error", "Configuration error. Please contact support.") :
        err.code === "auth/too-many-requests" ? t("LoginScreen.too_many_requests", "Too many attempts. Try again later.") :
        err.code === "auth/invalid-phone-number" ? t("LoginScreen.invalid_phone_format", "Invalid phone number.") :
        t("LoginScreen.failed_send_otp", "Failed to send OTP. Try again.");
      setMessage(msg); setMessageType("error"); setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
      if (window.recaptchaVerifier) { try { window.recaptchaVerifier.clear(); } catch (_) {} window.recaptchaVerifier = null; }
    }
    setLoginLoading(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      setMessage(t("LoginScreen.enter_valid_otp", "Please enter 6-digit OTP."));
      setMessageType("error"); setShowToast(true);
      setTimeout(() => setShowToast(false), 4000); return;
    }
    setLoginLoading(true); clearAlert();
    try {
      const credential = PhoneAuthProvider.credential(verificationId, otp);
      const result = await signInWithCredential(auth, credential);
      if (!result.user) {
        setMessage(t("LoginScreen.otp_verification_failed", "Verification failed. Try again."));
        setMessageType("error"); setShowToast(true);
        setTimeout(() => setShowToast(false), 4000);
        setLoginLoading(false); return;
      }
      const data = await getUserByMobile(phone);
      if (data?.responseCode === 200 && data?.responseData?.length > 0) {
        const user = data.responseData[0];
        localStorage.setItem("user", JSON.stringify(user));
        const token = await requestNotificationPermission();
        if (token) {
          const registered = await registerFCMToken(user.spId, token);
          if (!registered) await updateFCMToken(user.spId, token);
        }
        const ms = await getMembershipStatus(user.spId);
        navigate(ms?.responseCode === 200 && ms?.responseData ? "/dashboard" : "/membership");
      } else {
        setMessage(data.responseMessage || t("LoginScreen.user_not_found", "User not found. Please register."));
        setMessageType("error"); setShowToast(true);
        setTimeout(() => setShowToast(false), 4000);
      }
    } catch (err) {
      const msg =
        err.code === "auth/invalid-verification-code" ? t("LoginScreen.invalid_otp", "Invalid OTP. Please check.") :
        err.code === "auth/code-expired" ? t("LoginScreen.otp_expired", "OTP expired. Request a new one.") :
        t("LoginScreen.otp_verification_failed", "Verification failed.");
      setMessage(msg); setMessageType("error"); setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    }
    setLoginLoading(false);
  };

  const handleChangeNumber = () => { setOtpSent(false); setOtp(""); clearAlert(); setResendTimer(0); };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterForm((p) => ({ ...p, [name]: name === "phone" ? value.replace(/\D/g, "").slice(0, 10) : value }));
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (registerForm.phone.length !== 10) {
      setMessage(t("RegisterScreen.phone_10_digits", "Phone number must be exactly 10 digits"));
      setMessageType("error"); setShowToast(true);
      setTimeout(() => setShowToast(false), 4000); return;
    }
    setRegisterLoading(true); clearAlert();
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 10000);
    try {
      const data = await registerUserApi(registerForm, ctrl);
      clearTimeout(tid);
      const ok = data && (data.responseCode === 0 || data.responseCode === 200) && data.responseMessage === "success";
      const msg = data.responseMessage || t("RegisterScreen.registration_failed", "Registration failed");
      setMessage(msg); setMessageType(ok ? "success" : "error"); setShowToast(true);
      if (ok && data.responseData?.[0]?.spId) {
        try { await activateFreeMembership(data.responseData[0].spId); } catch (_) {}
      }
      setTimeout(() => {
        setShowToast(false);
        if (ok) { setAuthMode("login"); setOtpSent(false); setOtp(""); setPhone(registerForm.phone); navigate("/login"); }
      }, ok ? 3000 : 4000);
    } catch (err) {
      clearTimeout(tid);
      const msg = err.name === "AbortError"
        ? t("RegisterScreen.request_timeout", "Request timed out. Please try again.")
        : t("RegisterScreen.registration_failed", "Registration failed");
      setMessage(msg); setMessageType("error"); setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    }
    setRegisterLoading(false);
  };

  const timerLabel = `${String(Math.floor(resendTimer / 60)).padStart(2, "0")}:${String(resendTimer % 60).padStart(2, "0")}`;

  return (
    <div className={styles.pageWrap}>
      <div id="recaptcha-container" />

      {/* ─────────────────────────────────────── CHOOSER ─── */}
      {authMode === "chooser" && (
        <div className={styles.chooserPage}>
          <div className={styles.chooserCard}>

            {/* LEFT */}
            <div className={styles.chooserLeft}>
              <img src={LOGO} alt="Bhada24" className={styles.cLogo} />

              <h1 className={styles.cTitle}>
                List Your Business<br />
                Get More Customers<br />
                <span className={styles.cAccent}>for FREE!</span>
              </h1>

              <p className={styles.cSubtitle}>
                {t("AuthScreen.select_subtitle", "Join thousands of service providers on Bhada24 and grow your business.")}
              </p>

              <ul className={styles.cFeatures}>
                {["100% Free Listing", "Reach Local Customers", "Get More Bookings", "Grow Your Business"].map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>

              <div className={styles.cActions}>
                <button className={styles.btnPrimary} onClick={() => openAuthForm("register")}>
                  {t("AuthScreen.list_now", "Create Account")}
                </button>
                <button className={styles.btnOutline} onClick={() => openAuthForm("login")}>
                  {t("AuthScreen.login_account", "Login to Your Account")}
                </button>
              </div>

              <p className={styles.cTrust}>
                {t("AuthScreen.trust", "Trusted by 10,000+ Service Providers")}
              </p>
            </div>

            {/* RIGHT – phone mockup */}
            <div className={styles.chooserRight}>
              <div className={styles.phoneScene}>
                <div className={styles.phoneMock}>
                  <div className={styles.phonePill} />
                  <div className={styles.phoneBar} />
                  <div className={styles.phoneContent}>
                    <p className={styles.phoneBizName}>Your Business</p>
                    <p className={styles.phoneRating}>Rating 4.8</p>
                    <div className={styles.phoneMapArea} />
                  </div>
                </div>
                <span className={`${styles.svcTag} ${styles.svcTagA}`}>Cab Service</span>
                <span className={`${styles.svcTag} ${styles.svcTagB}`}>Sound System</span>
                <span className={`${styles.svcTag} ${styles.svcTagC}`}>Tent House</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────── LOGIN / OTP ─── */}
      {authMode === "login" && (
        <div className={styles.authPage}>
          <button className={styles.backBtn} onClick={goToChooser}>
            <FaArrowLeft size={11} />{t("AuthScreen.back", "Back")}
          </button>

          <div className={styles.loginCard}>
            <img src={LOGO} alt="Bhada24" className={styles.cardLogo} />

            {otpSent ? (
              <>
                <h2 className={styles.cardTitle}>{t("LoginScreen.verify_mobile", "Verify Your Mobile Number")}</h2>
                <p className={styles.cardSub}>{t("LoginScreen.otp_sent_desc", "We have sent a 6-digit OTP to")}</p>
                <p className={styles.phoneHighlight}>+91 {phone}</p>
              </>
            ) : (
              <>
                <h2 className={styles.cardTitle}>{t("LoginScreen.welcome_back", "Welcome Back!")}</h2>
                <p className={styles.cardSub}>{t("LoginScreen.login_to_account", "Login to your account")}</p>
                <p className={styles.cardSub2}>{t("LoginScreen.enter_details", "Please enter your details to continue")}</p>
              </>
            )}

            <form onSubmit={otpSent ? handleVerifyOtp : handleSendOtp} className={styles.form}>
              {!otpSent ? (
                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>{t("LoginScreen.mobile_number", "Mobile Number")}</label>
                  <div className={styles.phoneInputContainer}>
                    <span className={styles.countryCode}>+91</span>
                    <input
                      type="tel"
                      placeholder={t("LoginScreen.enter_mobile", "Enter mobile number")}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                      className={styles.phoneInput}
                      maxLength={10}
                      inputMode="numeric"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className={styles.inputWrapper}>
                    <label className={styles.inputLabel}>{t("LoginScreen.enter_otp", "Enter OTP")}</label>
                    <OtpInput value={otp} onChange={setOtp} />
                  </div>
                  <button type="button" onClick={handleChangeNumber} className={styles.changeNumberButton}>
                    <FaArrowLeft size={11} />{t("LoginScreen.change_number", "Change Number")}
                  </button>
                  {resendTimer === 0 ? (
                    <button type="button" onClick={handleSendOtp} className={styles.resendOtpButton} disabled={loginLoading}>
                      <FaRedo size={11} style={{ marginRight: 5 }} />
                      {t("LoginScreen.resend_otp", "Resend OTP")}
                    </button>
                  ) : (
                    <p className={styles.resendTimerText}>
                      {t("LoginScreen.resend_in", "Resend OTP in")} {timerLabel}
                    </p>
                  )}
                </>
              )}

              <button type="submit" className={styles.submitButton} disabled={loginLoading}>
                {loginLoading ? (
                  <span className={styles.buttonContent}>
                    <ButtonSpinner size={17} color="#fff" type="dots" />
                    <span>{otpSent ? t("LoginScreen.verifying", "Verifying...") : t("LoginScreen.sending_otp", "Sending...")}</span>
                  </span>
                ) : otpSent ? t("LoginScreen.verify_continue", "Verify & Continue") : t("LoginScreen.send_otp", "Login")}
              </button>
            </form>

            {message && <div className={`${styles.messageBox} ${styles[messageType]}`}>{message}</div>}

            {!otpSent && (
              <div className={styles.trustRow}>
                <div className={styles.trustItem}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12l2.5 2.5L16 9"/></svg>
                  <p className={styles.trustTitle}>100% Free</p>
                  <p className={styles.trustDesc}>No Hidden Charges</p>
                </div>
                <div className={styles.trustItem}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  <p className={styles.trustTitle}>Trusted Platform</p>
                  <p className={styles.trustDesc}>By Thousands</p>
                </div>
                <div className={styles.trustItem}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  <p className={styles.trustTitle}>24/7 Support</p>
                  <p className={styles.trustDesc}>We're Here to Help</p>
                </div>
              </div>
            )}

            {otpSent && (
              <p className={styles.secureNote}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:5,flexShrink:0}}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                {t("LoginScreen.secure_note", "Your number is safe with us. We never share your information.")}
              </p>
            )}

            <div className={styles.footer}>
              <span className={styles.footerText}>
                {t("LoginScreen.no_account", "Don't have an account?")}
                <button type="button" onClick={() => openAuthForm("register")} className={styles.switchButton}>
                  {t("LoginScreen.sign_up", "Register Now")}
                </button>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────── REGISTER ─── */}
      {authMode === "register" && (
        <div className={styles.authPage}>
          <button className={styles.backBtn} onClick={goToChooser}>
            <FaArrowLeft size={11} />{t("AuthScreen.back", "Back")}
          </button>

          <div className={styles.registerCard}>
            {/* form side */}
            <div className={styles.regLeft}>
              <img src={LOGO} alt="Bhada24" className={styles.cardLogo} />
              <h2 className={styles.cardTitle}>{t("RegisterScreen.create_account", "Create Your Account")}</h2>
              <p className={styles.cardSub}>{t("RegisterScreen.register_subtitle", "Join as a service provider and start getting customers")}</p>

              <form onSubmit={handleRegisterSubmit} className={styles.form}>
                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>{t("RegisterScreen.full_name", "Full Name")}</label>
                  <input
                    type="text" name="name"
                    placeholder={t("RegisterScreen.enter_name", "Enter your full name")}
                    value={registerForm.name} onChange={handleRegisterChange}
                    className={styles.input} required
                  />
                </div>

                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>{t("RegisterScreen.phone_number", "Mobile Number")}</label>
                  <div className={styles.phoneInputContainer}>
                    <span className={styles.countryCode}>+91</span>
                    <input
                      type="tel" name="phone"
                      placeholder={t("RegisterScreen.enter_phone", "Enter mobile number")}
                      value={registerForm.phone} onChange={handleRegisterChange}
                      className={styles.phoneInput} maxLength={10} inputMode="numeric" required
                    />
                  </div>
                </div>

                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>
                    {t("RegisterScreen.email", "Email Address")}
                    <span className={styles.optTag}> (Optional)</span>
                  </label>
                  <input
                    type="email" name="email"
                    placeholder={t("RegisterScreen.enter_email", "Enter email address")}
                    value={registerForm.email} onChange={handleRegisterChange}
                    className={styles.input}
                  />
                </div>

                <div className={styles.membershipBanner}>
                  <div className={styles.radioButton}>
                    <div className={styles.radioOuter}><div className={styles.radioInner} /></div>
                  </div>
                  <div className={styles.membershipContent}>
                    <div className={styles.membershipHeader}>
                      <span className={styles.membershipTitle}>{t("RegisterScreen.free_membership", "1-Year Free Starter Plan")}</span>
                      <span className={styles.membershipBadge}>FREE</span>
                    </div>
                    <p className={styles.membershipSubtitle}>
                      {t("RegisterScreen.membership_benefits", "Boost visibility with premium tools and priority support")}
                    </p>
                  </div>
                </div>

                <button type="submit" className={styles.submitButton} disabled={registerLoading}>
                  {registerLoading ? (
                    <span className={styles.buttonContent}>
                      <ButtonSpinner size={17} color="#fff" type="dots" />
                      <span>{t("RegisterScreen.registering", "Creating account...")}</span>
                    </span>
                  ) : t("RegisterScreen.register", "Register as Provider")}
                </button>
              </form>

              {message && <div className={`${styles.messageBox} ${styles[messageType]}`}>{message}</div>}

              <div className={styles.footer}>
                <span className={styles.footerText}>
                  {t("RegisterScreen.already_have_account", "Already have an account?")}
                  <button type="button" onClick={() => openAuthForm("login")} className={styles.switchButton}>
                    {t("RegisterScreen.login", "Login Now")}
                  </button>
                </span>
              </div>
            </div>

            {/* illustration side */}
            <div className={styles.regRight}>
              <div className={styles.regIllust}>
                <div className={styles.regIconRing}>
                  <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                    <rect x="14" y="26" width="28" height="20" rx="3" fill="#4f46e5" />
                    <rect x="20" y="34" width="10" height="12" rx="2" fill="white" />
                    <path d="M8 26 L28 10 L48 26H8Z" fill="#6d28d9" />
                  </svg>
                </div>
                <h3 className={styles.regIllustTitle}>
                  {t("AuthScreen.reg_illust_title", "Start Your Journey with Bhada24")}
                </h3>
                <ul className={styles.regIllustList}>
                  <li>{t("AuthScreen.reg_f1", "Create your free business listing")}</li>
                  <li>{t("AuthScreen.reg_f2", "Connect with local customers")}</li>
                  <li>{t("AuthScreen.reg_f3", "Grow your business online")}</li>
                </ul>
                <svg className={styles.cityIllust} width="240" height="88" viewBox="0 0 240 88" fill="none">
                  <rect x="0"   y="28" width="26" height="60" rx="3" fill="#c4b5fd" opacity="0.45"/>
                  <rect x="34"  y="46" width="20" height="42" rx="3" fill="#a78bfa" opacity="0.4"/>
                  <rect x="62"  y="16" width="34" height="72" rx="3" fill="#7c3aed" opacity="0.3"/>
                  <rect x="104" y="34" width="26" height="54" rx="3" fill="#c4b5fd" opacity="0.4"/>
                  <rect x="138" y="50" width="20" height="38" rx="3" fill="#a78bfa" opacity="0.35"/>
                  <rect x="166" y="26" width="30" height="62" rx="3" fill="#7c3aed" opacity="0.28"/>
                  <rect x="204" y="42" width="22" height="46" rx="3" fill="#c4b5fd" opacity="0.32"/>
                  <circle cx="79" cy="10" r="6" fill="#4f46e5"/>
                  <line x1="79" y1="16" x2="79" y2="22" stroke="#4f46e5" strokeWidth="2"/>
                  <circle cx="117" cy="24" r="5" fill="#6d28d9"/>
                  <line x1="117" y1="29" x2="117" y2="36" stroke="#6d28d9" strokeWidth="2"/>
                  <circle cx="44" cy="36" r="5" fill="#4f46e5" opacity="0.7"/>
                  <line x1="44" y1="41" x2="44" y2="47" stroke="#4f46e5" strokeWidth="2" opacity="0.7"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      {showToast && message && (
        <Toast message={message} success={messageType === "success"} onClose={() => { setShowToast(false); setMessage(""); }} />
      )}
    </div>
  );
}
